import { useMutation, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link, useRouter } from '@tanstack/react-router'
import { App, Button, Form, Input } from 'antd'
import { createSchemaFieldRule } from 'antd-zod'
import { LockKeyhole, Mail, ShieldCheck } from 'lucide-react'
import { signInSchema, type SignInInput } from '@super-mega/server/contracts/auth'
import {
  AUTH_FIELD_CLASS_NAME,
  AUTH_FORM_CLASS_NAMES,
  AuthFormHeader,
  AuthShell,
  getAuthError
} from '@/features/auth/index.ts'
import { useTRPC } from '@/trpc/react.ts'
import { getSafeAuthRedirect } from './-redirect.ts'

const signInRule = createSchemaFieldRule(signInSchema)
const loginErrorToastKey = 'auth-login-error'

export const Route = createFileRoute('/auth/signin')({
  component: SignInPage,
  head: () => ({
    meta: [{ title: '登录 · Super Mega 租赁管理一体化智能系统' }]
  })
})

function SignInPage() {
  const { redirect: redirectAfterLogin } = Route.useSearch()
  const trpc = useTRPC()
  const router = useRouter()
  const queryClient = useQueryClient()
  const { message: messageApi } = App.useApp()

  const login = useMutation(trpc.auth.login.mutationOptions())

  const handleSubmit = async (values: SignInInput) => {
    messageApi.destroy(loginErrorToastKey)

    try {
      const session = await login.mutateAsync(values)
      queryClient.setQueryData(trpc.auth.session.queryKey(), session)
      if (session.status === 'active' && session.member) {
        await router.navigate({
          href: getSafeAuthRedirect(redirectAfterLogin),
          replace: true
        })
      } else {
        await router.navigate({ to: '/auth/status', replace: true })
      }
    } catch (mutationError) {
      const presentation = getAuthError(mutationError, '登录')
      void messageApi.open({
        content: presentation.message,
        duration: 3,
        key: loginErrorToastKey,
        type: 'error'
      })
    }
  }

  return (
    <AuthShell className="max-w-[27rem]">
      <AuthFormHeader description="继续查看客户、订单和待处理事项。" title="欢迎回来" />

      <Form<SignInInput>
        classNames={AUTH_FORM_CLASS_NAMES}
        disabled={login.isPending}
        initialValues={{ email: '', password: '' }}
        layout="vertical"
        onFinish={(values) => void handleSubmit(values)}
        requiredMark={false}
        scrollToFirstError={{ block: 'nearest', scrollMode: 'if-needed', focus: true }}
        size="large"
      >
        <Form.Item<SignInInput> className={AUTH_FIELD_CLASS_NAME} label="登录邮箱" name="email" rules={[signInRule]}>
          <Input
            allowClear
            autoComplete="email"
            autoFocus
            autoCapitalize="none"
            maxLength={320}
            placeholder="name@company.com"
            prefix={<Mail aria-hidden className="size-4" strokeWidth={1.8} />}
            spellCheck={false}
          />
        </Form.Item>

        <Form.Item<SignInInput> className={AUTH_FIELD_CLASS_NAME} label="密码" name="password" rules={[signInRule]}>
          <Input.Password
            autoComplete="current-password"
            maxLength={1024}
            placeholder="请输入密码"
            prefix={<LockKeyhole aria-hidden className="size-4" strokeWidth={1.8} />}
          />
        </Form.Item>

        <div className="mb-5 text-right text-sm">
          <Link
            className="font-medium !text-[var(--primary)] hover:underline"
            search={{ redirect: redirectAfterLogin }}
            to="/auth/forgot-password"
          >
            忘记密码？
          </Link>
        </div>

        <Button block className="!h-12 !font-semibold" htmlType="submit" loading={login.isPending} type="primary">
          登录工作台
        </Button>
      </Form>

      <div className="text-content-muted mt-5 text-center text-sm">
        还没有账号？
        <Link
          className="ml-1 font-medium !text-[var(--primary)] hover:underline"
          search={{ redirect: redirectAfterLogin }}
          to="/auth/signup"
        >
          创建账号
        </Link>
      </div>

      <div className="border-border text-content-soft mt-6 flex items-start gap-2.5 border-t pt-4 text-xs leading-5">
        <ShieldCheck aria-hidden className="text-accent mt-0.5 size-4 shrink-0" strokeWidth={1.8} />
        <span>企业私有实例安全访问，登录信息仅用于身份校验。</span>
      </div>
    </AuthShell>
  )
}
